/**
 * BillingV2CheckoutTest.jsx – Interner Stripe Testcheckout
 *
 * Nur in Preview, nur für Superadmin.
 * Dient ausschließlich der technischen Abnahme des Stripe-Testcheckouts.
 * Keine Landingpage, kein Marketing.
 *
 * Route: /superadmin/billing-v2-checkout-test
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, CreditCard, Package, Users, MapPin, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const IS_PREVIEW = process.env.REACT_APP_BACKEND_URL?.includes('preview.emergentagent.com') ||
                   process.env.REACT_APP_BACKEND_URL?.includes('localhost');

const PACKAGES = [
  { slug: 'starter',         label: 'Starter',         price_month: 29.90, price_year: 299.00 },
  { slug: 'business',        label: 'Business',        price_month: 39.90, price_year: 399.00 },
  { slug: 'professional_ai', label: 'Professional AI', price_month: 59.90, price_year: 599.00 },
];

const ADDONS = [
  { slug: 'employees_plus_5',  label: '+5 Mitarbeiter',  price_month: 5.99,  price_year: 59.90 },
  { slug: 'employees_plus_10', label: '+10 Mitarbeiter', price_month: 9.99,  price_year: 99.90 },
  { slug: 'employees_plus_20', label: '+20 Mitarbeiter', price_month: 17.99, price_year: 179.90 },
];

export default function BillingV2CheckoutTest() {
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Formular
  const [packageSlug, setPackageSlug] = useState('starter');
  const [interval, setInterval] = useState('month');
  const [locationSlots, setLocationSlots] = useState(1);
  const [addonQuantities, setAddonQuantities] = useState({ employees_plus_5: 0, employees_plus_10: 0, employees_plus_20: 0 });

  // Quote
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { loadStripeStatus(); }, []);

  const loadStripeStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(`${API}/api/superadmin/billing-v2/stripe-test-status`, { headers: authHeader() });
      setStripeStatus(res.data);
    } catch (e) {
      toast.error('Stripe-Status konnte nicht geladen werden');
    } finally { setLoadingStatus(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/api/superadmin/billing-v2/sync-stripe-test`, {}, { headers: authHeader() });
      toast.success('Stripe-Sync abgeschlossen');
      await loadStripeStatus();
    } catch (e) {
      toast.error('Sync fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally { setSyncing(false); }
  };

  const buildAddonList = () =>
    Object.entries(addonQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([slug, quantity]) => ({ slug, quantity }));

  const handleGetQuote = async () => {
    setLoadingQuote(true);
    setQuote(null);
    try {
      const res = await axios.post(`${API}/api/billing-v2/checkout/quote`, {
        package_slug: packageSlug,
        billing_interval: interval,
        requested_location_slots: locationSlots,
        employee_addons: buildAddonList(),
      }, { headers: authHeader() });
      setQuote(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message;
      toast.error('Quote Fehler: ' + msg);
    } finally { setLoadingQuote(false); }
  };

  const handleStartCheckout = async () => {
    if (!quote) { toast.error('Bitte zuerst Preisvorschau laden'); return; }
    setLoadingSession(true);
    try {
      const res = await axios.post(`${API}/api/billing-v2/checkout/session`, {
        package_slug: packageSlug,
        billing_interval: interval,
        requested_location_slots: locationSlots,
        employee_addons: buildAddonList(),
        frontend_url: window.location.origin,
      }, { headers: authHeader() });
      window.location.href = res.data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message;
      toast.error('Checkout Fehler: ' + msg);
      setLoadingSession(false);
    }
  };

  if (!IS_PREVIEW) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-red-600">
          Diese Seite ist nur in der Preview-Umgebung verfügbar.
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Billing V2 – Stripe Testcheckout</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Nur Preview · Nur Superadmin · Ausschließlich Stripe-Testmodus
          </p>
        </div>

        {/* Stripe Sync Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Stripe Test-Sync Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStatus ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : stripeStatus ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {stripeStatus.stripe_sync_ready ? (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle2 className="h-4 w-4" /> Bereit – alle Stripe Price IDs vorhanden
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-600 text-sm font-medium">
                      <AlertTriangle className="h-4 w-4" /> Sync erforderlich
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {stripeStatus.packages?.map(p => (
                    <div key={p.slug} className={`p-2 rounded border text-center ${p.stripe_price_id_monthly ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                      <p className="font-semibold">{p.name || p.slug}</p>
                      <p className={p.stripe_price_id_monthly ? 'text-green-600' : 'text-red-600'}>
                        {p.stripe_price_id_monthly ? '✓ Sync' : '✗ fehlt'}
                      </p>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing}>
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Stripe-Sync ausführen
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* Checkout-Konfigurator */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4" />
              Checkout konfigurieren
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Paket */}
            <div>
              <label className="text-sm font-medium mb-2 block">Paket</label>
              <div className="grid grid-cols-3 gap-2">
                {PACKAGES.map(p => (
                  <button key={p.slug}
                    onClick={() => setPackageSlug(p.slug)}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      packageSlug === p.slug
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-bold">{p.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {interval === 'year' ? `${p.price_year.toFixed(2)} €/Jahr` : `${p.price_month.toFixed(2)} €/Mon`}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Interval */}
            <div>
              <label className="text-sm font-medium mb-2 block">Abrechnungsintervall</label>
              <div className="flex gap-2">
                {[{ v: 'month', l: '🎉 1 Monat kostenlos' }, { v: 'year', l: 'Jährlich (2 Monate gratis)' }].map(opt => (
                  <button key={opt.v}
                    onClick={() => setInterval(opt.v)}
                    className={`px-4 py-2 rounded-lg border-2 text-sm transition-all ${
                      interval === opt.v ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Standorte */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Standortplätze
              </label>
              <div className="flex items-center gap-3">
                <button onClick={() => setLocationSlots(Math.max(1, locationSlots - 1))}
                  className="w-8 h-8 rounded-full border-2 border-gray-300 font-bold hover:border-blue-400">-</button>
                <span className="text-2xl font-bold w-8 text-center">{locationSlots}</span>
                <button onClick={() => setLocationSlots(Math.min(10, locationSlots + 1))}
                  className="w-8 h-8 rounded-full border-2 border-gray-300 font-bold hover:border-blue-400">+</button>
                <span className="text-sm text-muted-foreground">
                  {locationSlots === 1 ? '1 Standort (inklusive)' : `${locationSlots} Standorte (${locationSlots - 1} kostenpflichtig)`}
                </span>
              </div>
            </div>

            {/* MA Add-ons */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Users className="h-4 w-4" /> Mitarbeiter-Add-ons
              </label>
              <div className="space-y-2">
                {ADDONS.map(addon => (
                  <div key={addon.slug} className="flex items-center gap-3">
                    <span className="text-sm w-40">{addon.label}</span>
                    <span className="text-xs text-muted-foreground w-28">
                      {interval === 'year' ? `${addon.price_year.toFixed(2)} €/Jahr` : `${addon.price_month.toFixed(2)} €/Mon`}
                    </span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setAddonQuantities(prev => ({ ...prev, [addon.slug]: Math.max(0, (prev[addon.slug] || 0) - 1) }))}
                        className="w-7 h-7 rounded border font-bold text-sm hover:bg-gray-100">-</button>
                      <span className="w-6 text-center text-sm font-semibold">{addonQuantities[addon.slug] || 0}</span>
                      <button onClick={() => setAddonQuantities(prev => ({ ...prev, [addon.slug]: Math.min(5, (prev[addon.slug] || 0) + 1) }))}
                        className="w-7 h-7 rounded border font-bold text-sm hover:bg-gray-100">+</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleGetQuote} disabled={loadingQuote} variant="outline" className="w-full">
              {loadingQuote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Preisvorschau berechnen
            </Button>
          </CardContent>
        </Card>

        {/* Preisvorschau */}
        {quote && (
          <Card className="border-2 border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-blue-800">
                Preisvorschau – {quote.package_name} · {quote.interval_label}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Paket ({quote.package_name})</span>
                  <span className="font-medium">{quote.base_package_net.toFixed(2)} € Netto</span>
                </div>
                {quote.additional_locations > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>{quote.additional_locations}× Zusatzstandort à {quote.additional_location_unit_net.toFixed(2)} €</span>
                    <span>{quote.additional_locations_total_net.toFixed(2)} € Netto</span>
                  </div>
                )}
                {quote.employee_addons?.map(a => (
                  <div key={a.slug} className="flex justify-between text-muted-foreground">
                    <span>{a.quantity}× {a.label}</span>
                    <span>{a.line_net_eur.toFixed(2)} € Netto</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-3 space-y-1">
                <div className="flex justify-between font-semibold">
                  <span>Netto gesamt</span>
                  <span>{quote.subtotal_net.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>zzgl. {quote.tax_rate}% MwSt.</span>
                  <span>+{quote.tax_amount.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Brutto gesamt</span>
                  <span className="text-blue-700">{quote.total_gross.toFixed(2)} € / {quote.interval_unit}</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground bg-gray-50 rounded p-2">
                Mitarbeiter inklusive: {quote.total_employee_limit}
                ({quote.requested_location_slots} Standorte × {quote.employees_per_location} + {quote.addon_employee_total} Add-ons)
              </div>

              <Button
                onClick={handleStartCheckout}
                disabled={loadingSession || !stripeStatus?.stripe_sync_ready}
                className="w-full h-12 text-base"
                size="lg"
              >
                {loadingSession ? (
                  <><Loader2 className="h-5 w-5 animate-spin mr-2" />Checkout wird geöffnet...</>
                ) : (
                  <><CreditCard className="h-5 w-5 mr-2" />Stripe Testcheckout öffnen</>
                )}
              </Button>
              {!stripeStatus?.stripe_sync_ready && (
                <p className="text-xs text-amber-600 text-center">
                  Bitte zuerst Stripe-Sync ausführen
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
